import { useRef, useEffect } from "react";
import { Col, Row, Button } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { Formik, Form } from "formik";
import _get from "lodash/get";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  TextInput,
  ImageUpload,
  TextEditor,
  MultiImageUpload,
  Select,
} from "components/common";
import Helper from "utils/helpers";
import {
  ALLOW_PAY_VIEGO,
  PROMOTION_ACTIVE,
  WORKING_TIME_TEXT,
} from "utils/constants";
import AutoComplete from "components/common/Form/AutoComplete";
import ChangeWorkingTimeModal from "components/ChangeWorkingTimeModal";
import {
  setChangeWorkingTimeModal,
  setChangeOpenningTimeModal,
} from "providers/GeneralProvider/slice";
import i18n from "i18n";
import {
  getDiscountConditionsRequest,
  getDiscountPercentagesRequest,
} from "providers/StoreProvider/slice";
import { RadioField } from "components/common/Form";
import ChangeOpenningTimeModal from "components/ChangeOpenningTimeModal";
import moment from "moment";
import { editRestaurantValidate, createRestaurantValidate } from "./validate";
import "./styles.less";

const RestaurantEditForm = ({
  initialValues = {},
  onCancel,
  onSubmit,
  isCreating,
}) => {
  const dispatch = useDispatch();
  const formikRef = useRef(null);

  const discountConditions = useSelector(
    (state) => state.store.discountConditions
  );
  const discountPercentages = useSelector(
    (state) => state.store.discountPercentages
  );
  const discountConditionsArr = discountConditions.map((item) => {
    if (item.value === "EVERY_DAY") {
      return {
        value: item.value,
        label: "Hàng ngày",
      };
    }
    if (item.value === "EVERY_DAY_EXCLUDING_HOLIDAYS") {
      return {
        value: item.value,
        label: "Các ngày trong tuần không bao gồm lễ, tết",
      };
    }
    if (item.value === "MONDAY_TO_THURSDAY") {
      return {
        value: item.value,
        label: "Từ thứ 2 tới thứ 5 không bao gồm ngày nghỉ, lễ, tết ",
      };
    }
    if (item.value === "MONDAY_TO_FRIDAY") {
      return {
        value: item.value,
        label: "Từ thứ 2 đến thứ 6 không bao gồm lễ, tết",
      };
    }
    if (item.value === "CUSTOM") {
      return {
        value: item.value,
        label: "Tùy chọn",
      };
    }
    return null;
  });
  useEffect(() => {
    dispatch(getDiscountConditionsRequest());
    dispatch(getDiscountPercentagesRequest());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const workingTimeModal = useSelector(
    (state) => state.general.workingTimeModal
  );
  const openningTimeModal = useSelector(
    (state) => state.general.openningTimeModal
  );

  const formattedInitValues = {
    name: _get(initialValues, "name", ""),
    address: _get(initialValues, "address", ""),
    phone: _get(initialValues, "phone", ""),
    banners: null,
    thumbnail: initialValues.thumbnail
      ? {
          url: initialValues.thumbnail,
          objectId: initialValues.thumbnail,
        }
      : null,
    open: Helper.convertMinutesToMoment(_get(initialValues, "open", "")),
    close: Helper.convertMinutesToMoment(_get(initialValues, "close", "")),
    startDay: _get(initialValues, "startDay", ""),
    endDay: _get(initialValues, "endDay", ""),
    description: _get(initialValues, "description", ""),
    workingTimeMethod: _get(initialValues, "workingTimeMethod", ""),
    workingTime: _get(initialValues, "workingTime", []),
    businessImages: _get(initialValues, "businessImages", []),
    discountCondition: initialValues?.discountCondition?.value || null,
    discountPercentage: initialValues?.discountPercentage?.value || null,
    hasPromotion: _get(initialValues, "hasPromotion", false),
    isAllowedPayWithVieGo: _get(initialValues, "isAllowedPayWithVieGo", false),
    customDiscountCondition: _get(initialValues, "customDiscountCondition", {}),
    openningTimeData:
      initialValues?.customDiscountCondition?.discountTime || [],
  };
  const remainningPromotionTime = !isCreating
    ? Helper.calculateRemainningPromotionTime(
        formattedInitValues?.openningTimeData || []
      )
    : null;

  const handleChangeWorkingTimeModal = (values) => {
    dispatch(
      setChangeWorkingTimeModal({
        isOpen: true,
        data: {
          workingTimeMethod: values.workingTimeMethod,
          workingTime: values.workingTime,
        },
        category: "restaurant",
      })
    );
  };

  const handleChangeOpenningTimeModal = (values) => {
    dispatch(
      setChangeOpenningTimeModal({
        isOpen: true,
        data: {
          openningTimeData: values?.openningTimeData,
        },
        category: "restaurant",
      })
    );
  };

  const handleChangeWorkingTime = (values) => {
    formikRef.current?.setFieldValue(
      "workingTimeMethod",
      values.workingTimeMethod
    );
    formikRef.current?.setFieldValue("workingTime", values.workingTime);
  };

  const handleChangeOpenningTime = ({ openningTimeData }) => {
    formikRef.current?.setFieldValue("openningTimeData", openningTimeData);
  };

  const handleChangeDiscountCondition = (discountCondition, values) => {
    if (discountCondition === "CUSTOM") {
      handleChangeOpenningTimeModal(values);
    } else {
      formikRef.current?.setFieldValue("openningTimeData", []);
    }
  };

  return (
    <Formik
      validateOnMount
      initialValues={formattedInitValues}
      enableReinitialize
      validationSchema={
        isCreating ? createRestaurantValidate : editRestaurantValidate
      }
      onSubmit={onSubmit}
      innerRef={formikRef}
    >
      {({ handleSubmit, values, isValid }) => (
        <Form>
          {workingTimeModal.isOpen && (
            <ChangeWorkingTimeModal
              workingTimeModal={workingTimeModal}
              handleChangeWorkingTime={handleChangeWorkingTime}
            />
          )}
          {openningTimeModal.isOpen && (
            <ChangeOpenningTimeModal
              openningTimeModal={openningTimeModal}
              handleChangeOpenningTime={handleChangeOpenningTime}
              fieldAlwaysOpen="alwaysDiscount"
              fieldAlwaysClose="noDiscount"
              canDisableSelectTimeSlot={!isCreating}
            />
          )}
          <Row className="restaurant-edit-component-wrapper">
            <Row className="restaurant-edit-info-container">
              <Col span={14} className="info-section">
                {isCreating && (
                  <div className="m-title-text">Thông tin cơ bản</div>
                )}
                <Row style={{ flexWrap: "nowrap" }}>
                  <Col>
                    <ImageUpload name="thumbnail" />
                  </Col>
                  <Col
                    flex="auto"
                    className="restaurant-info"
                    style={{ paddingRight: "6%" }}
                  >
                    <Row className="info-row" gutter={8}>
                      <Col className="info-col" span={16}>
                        <div className="s-text">Tên nhà hàng</div>
                        <TextInput name="name" size="small" required />
                      </Col>
                    </Row>
                    <Row className="info-row" gutter={8}>
                      <Col className="info-col" span={24}>
                        <div className="s-text">Giờ làm việc</div>
                        <Row className="time-working-wrapper">
                          <TextInput
                            className="time-working-title"
                            name="workingTimeMethod"
                            size="small"
                            required
                            disabled
                            value={WORKING_TIME_TEXT[values.workingTimeMethod]}
                          />
                          <SettingOutlined
                            className="setting-icon"
                            onClick={() => handleChangeWorkingTimeModal(values)}
                          />
                        </Row>
                      </Col>
                    </Row>
                    <Row className="info-row" gutter={8}>
                      <Col className="info-col" span={24}>
                        <div className="s-text">Địa chỉ</div>
                        <AutoComplete name="address" size="small" required />
                      </Col>
                    </Row>
                    <Row className="info-row" gutter={8}>
                      <Col className="info-col" span={8}>
                        <div className="s-text">Số điện thoại</div>
                        <TextInput name="phone" size="small" required />
                      </Col>
                    </Row>
                    <Col className="info-row" gutter={8}>
                      <Col className="info-col" span={8}>
                        <div className="s-text">
                          {i18n.t("menu.togglePromotion")}
                        </div>
                        <RadioField
                          name="hasPromotion"
                          className="working-time-options"
                          disabled={
                            values.hasPromotion
                              ? remainningPromotionTime !== null
                              : undefined
                          }
                          options={PROMOTION_ACTIVE}
                        />
                      </Col>
                    </Col>
                    {values.hasPromotion && (
                      <>
                        <Col className="info-row" gutter={8}>
                          <Col className="info-col" span={16}>
                            <div className="s-text">Điều kiện khuyến mãi</div>
                            <Select
                              style={{ width: 400 }}
                              name="discountCondition"
                              required
                              disabled={remainningPromotionTime !== null}
                              options={discountConditionsArr}
                              onChange={(discountCondition) => {
                                handleChangeDiscountCondition(
                                  discountCondition,
                                  values
                                );
                              }}
                            />
                          </Col>
                        </Col>
                        {remainningPromotionTime && (
                          <div className="remainning-promotion-time-wrapper">
                            {i18n.t("productDetail.canNotChangePromotionTime", {
                              remainningTime: remainningPromotionTime,
                              day: moment(new Date()).format("dddd"),
                            })}
                          </div>
                        )}
                        {(values?.discountCondition === "CUSTOM" ||
                          values?.discountCondition?.value === "CUSTOM") &&
                          values?.openningTimeData?.length > 0 && (
                            <Col className="info-row">
                              <Button
                                onClick={() =>
                                  handleChangeOpenningTimeModal(values)
                                }
                                type={"primary"}
                              >
                                {i18n.t("menu.viewTimePromotion")}
                              </Button>
                            </Col>
                          )}

                        <Col className="info-row" gutter={8}>
                          <Col className="info-col" span={16}>
                            <div className="s-text">Tỉ lệ khuyến mãi</div>
                            <Select
                              style={{ width: 400 }}
                              name="discountPercentage"
                              required
                              options={discountPercentages}
                            />
                          </Col>
                        </Col>
                      </>
                    )}

                    <Col className="info-row" gutter={8}>
                      <Col className="info-col" span={12}>
                        <div className="s-text">Thanh toán bằng điểm VieGo</div>
                        <RadioField
                          name="isAllowedPayWithVieGo"
                          className="working-time-options"
                          // onChange={handleOnchangeIsPromotionActive}
                          options={ALLOW_PAY_VIEGO}
                        />
                      </Col>
                    </Col>
                    <Col className="info-row" gutter={8}>
                      <Row className="info-col" span={12}>
                        <MultiImageUpload
                          label="Ảnh cửa hàng"
                          name="businessImages"
                        />
                      </Row>
                    </Col>
                  </Col>
                </Row>
              </Col>
              {isCreating && (
                <Col span={6} className="banner-section">
                  <div className="m-title-text">Hình banner</div>
                  <ImageUpload name="banners" />
                </Col>
              )}
              <Col flex="auto">
                <Row gutter={8} justify="end">
                  {!isCreating && (
                    <Col>
                      <Button onClick={onCancel} type="primary" size="small">
                        HỦY
                      </Button>
                    </Col>
                  )}
                  <Col>
                    <Button
                      onClick={handleSubmit}
                      size="small"
                      disabled={!isValid}
                    >
                      LƯU
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="business-description-container">
              <div className="title">
                {i18n.t("entertainment.businessDescription")}
              </div>
              <TextEditor name="description" />
            </Row>
          </Row>
        </Form>
      )}
    </Formik>
  );
};
RestaurantEditForm.propTypes = {
  initialValues: PropTypes.object,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  isCreating: PropTypes.bool,
};
export default RestaurantEditForm;
